<template>
  <div class="w-full min-w-[200px] relative">
    <div
      v-if="canReset"
      class="absolute right-0 text-xs cursor-pointer bottom-full text-main-primary"
      @click="
        resetFilter();
      "
    >
      Reset Filter
    </div>
    <!-- Select  -->
    <button
      :id="`assetsTreeSelector-${uid}`"
      :ref="`assetsTreeSelector-${uid}`"
      :disabled="disableFilter"
      class="flex items-center justify-between w-full bg-white px-3 py-1.5 leading-tight border border-[#0000001A] focus:shadow-outline select-none rounded-md shadow-sm outline-none focus:border-main-primary focus:ring-main-primary disabled:bg-zinc-300 disabled:cursor-not-allowed disabled:pointer-events-none"
      @click.prevent="togglePanel"
    >
      <div class="flex flex-col items-start gap-1 text-xs font-normal">
        <span class="text-[#707C8ECC]">Filter By</span>
        <span class="flex items-center gap-x-1 text-primary">
          <FilterIcon custom-style="size-4 text-[#707C8ECC]" />
          Assets
        </span>
      </div>
      <div class="flex gap-2">
        <span class="text-gray-300">|</span>
        <ChevronDownSmall
          class="text-gray-500 duration-150 transform"
          :class="{ 'rotate-180': isOpen }"
        />
      </div>
    </button>
    <!-- Panel -->
    <div
      v-if="isOpen"
      :ref="`assetsTreePanel-${uid}`"
      class="fixed z-50 mt-0.5 bg-white border border-[#0000001A] rounded-s-md shadow-lg select-none"
      :style="{ top: selectorTop + 'px', left: selectorLeft + 'px', width: selectorWidth + 'px' }"
    >
      <form class="relative w-full p-2 flex flex-col gap-1.5">
        <span class="text-[#707C8ECC] text-xs">Asset Classes</span>
        <div class="max-h-[600px] h-auto overflow-y-auto flex flex-col gap-1">
          <div
            v-for="({ asset_class_title, asset_class_id }, index) in assetClasses"
            :key="`${asset_class_id}-${index}`"
            class="flex items-center justify-between px-2 py-1 text-sm"
          >
            <label
              :for="`class-checkbox-${index}-${uid}`"
              class="truncate cursor-pointer"
            >{{ asset_class_title
            }}</label>
            <input
              :id="`class-checkbox-${index}-${uid}`"
              v-model="assetClasses[index].checked"
              type="checkbox"
              class="p-1 rounded-full cursor-pointer bg-slate-100 border-1 border-slate-100 text-main-primary focus:ring-transparent"
              @change="onCheckAssetClass($event, asset_class_id)"
            >
          </div>
        </div>
        <section
          class="w-[258px] h-auto max-h-[600px] overflow-auto absolute left-full -top-[1px] z-50 pb-4 bg-white border border-[#0000001A] rounded-e-md shadow-lg p-2 pt-0"
        >
          <header class="sticky left-0 pt-2 pb-3">
            <div class="flex justify-between text-xs">
              <span class="text-[#707C8ECC]">Assets</span>
              <div class="text-[#707C8E] flex gap-1.5">
                <button
                  id="select-all-assets-button"
                  class="outline-none"
                  type="button"
                  @click="selectAllAssets"
                >
                  Select All
                </button>
                <button
                  id="uncheck-all-assets-button"
                  class="outline-none"
                  type="button"
                  @click="deselectAllAssets"
                >
                  Deselect All
                </button>
              </div>
            </div>
            <InputWithIcon
              :val="assetQuery"
              classes="mt-1 text-xs rounded-lg placeholder:text-xs"
              placeholder="Search..."
              @input="assetQuery = $event"
            >
              <template #icon>
                <SearchIcon custom-style="size-2.5" />
              </template>
            </InputWithIcon>
          </header>
          <AssetTreeNode
            :classes="assetClasses"
            :node="assetsTree"
            @toggle-children="handleToggleProp($event, 'expandedChildren')"
            @toggle-check-asset="handleCheckedAsset"
          />
        </section>
      </form>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'

export default {
  name: 'AssetsTreeSelector',

  props: {
    assets: {
      type: Array,
      required: true,
    },
    classes: {
      type: Array,
      required: true,
    },
    resetAsFilter: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['enable', 'selected'],

  data() {
    return {
      orgAssets: this.transformAssets(this.assets),
      assetClasses: this.transformClasses(this.classes),
      isOpen: false,
      selectorTop: 0,
      selectorLeft: 0,
      selectorWidth: 0,
      assetQuery: '',
      uid: null,
    }
  },

  computed: {
    canReset() {
      return !this.isOpen && this.resetAsFilter && this.isFiltersApplied
    },
    isFiltersApplied() {
      const numberOfFiltersChecked = [...this.orgAssets].filter(({ checked }) => checked).length
      // If the number of filters checked is the same as the org's asset, meaning everything is settled so technically no filter is applied
      return numberOfFiltersChecked !== this.orgAssets.length
    },
    disableFilter() {
      return this.disabled
    },
    assetsTree() {
      const map = new Map()
      const tree = []

      this.orgAssets.forEach((asset) => {
        map.set(asset.id, { ...asset, children: [] })
      })

      this.orgAssets.forEach(({ id, parent_node }) => {
        if (parent_node !== null) {
          const parent = map.get(parent_node)
          if (parent) {
            parent.children.push(map.get(id))
          }
        }
        else {
          tree.push(map.get(id))
        }
      })

      return tree[0]
    },
  },

  watch: {
    isOpen(val) {
      // This ensures that the DOM is updated and the selector is fully rendered before the event listener is added.
      nextTick(() => {
        if (val) {
          document.body.addEventListener('click', this.handleOutsideClick)
          document.body.addEventListener('keydown', this.handleEscapeKey)
          this.positionSelectorPanel()
          this.positionSelectorPanel = debounce(this.positionSelectorPanel, 50)
          document.addEventListener('scroll', this.positionSelectorPanel, true)
          window.addEventListener('resize', this.positionSelectorPanel, true)
        }
      })
    },
    classes(newClasses) {
      this.assetClasses = this.transformClasses(newClasses)
    },
    assets(newAssets) {
      this.orgAssets = this.transformAssets(newAssets)
    },
    assetQuery(newQuery) {
      // On asset search
      this.deselectAllAssets(true)

      if (newQuery) {
        this.orgAssets = this.orgAssets.map((asset) => {
          if (!asset.asset_name.toLowerCase().includes(newQuery.toLowerCase())) {
            asset.disabledSelection = true
          }
          return asset
        })
      }
    },
  },

  mounted() {
    this.uid = this.$.uid
  },

  beforeUnmount() {
    if (this.isOpen) this.killAllListeners()
  },

  methods: {
    handleToggleProp(assetId, key) {
      // Sync with the child component
      this.orgAssets = this.orgAssets.map((asset) => {
        if (asset.id === assetId) {
          asset[key] = !asset[key]
        }
        return asset
      })
    },
    onCheckAssetClass(event, assetClassId) {
      // UI Case 1: 'all'
      if (assetClassId === 'all') {
        if (event.target.checked) {
          // Deselect everything else
          this.checkOtherClassesCheckbox(false)
        }
      }
      // UI Case 2: anything other than 'all'
      if (assetClassId !== 'all') {
        if (event.target.checked) {
          const allAssetClassesAreChecked = [...this.assetClasses].filter(({ asset_class_id, checked }) => asset_class_id !== 'all' && checked).length === this.classes.length
          this.checkAllClassesCheckbox(allAssetClassesAreChecked)
        }
        else {
          this.checkAllClassesCheckbox(false)
        }
      }
      // Handle check logic
      const selectedClasses = [...this.assetClasses].filter(({ checked }) => checked).map(({ asset_class_id }) => asset_class_id)
      if (selectedClasses.includes('all')) {
        this.orgAssets = this.orgAssets.map((asset) => {
          asset.checked = true
          asset.disabledSelection = false
          return asset
        })
        this.$emit(
          'selected',
          [...this.orgAssets].map(({ id }) => id),
        )
        return
      }
      const assets = [...this.orgAssets].filter(({ asset_classification }) => selectedClasses.includes(asset_classification.id)).map(({ id }) => id)
      this.orgAssets = this.orgAssets.map((asset) => {
        asset.checked = false
        asset.disabledSelection = false
        return asset
      })
      this.checkAssetsOfClasses(selectedClasses, assets)
    },
    checkAllClassesCheckbox(check = true) {
      this.assetClasses = this.assetClasses.map((assetClass) => {
        if (assetClass.asset_class_id === 'all') {
          assetClass.checked = check
        }
        return assetClass
      })
    },
    checkOtherClassesCheckbox(check = true) {
      this.assetClasses = this.assetClasses.map((assetClass) => {
        if (assetClass.asset_class_id !== 'all') {
          assetClass.checked = check
        }
        return assetClass
      })
    },
    checkAssetsOfClasses(classes, assets) {
      this.orgAssets = this.orgAssets.map((asset) => {
        if (assets.includes(asset.id)) {
          asset.disabledSelection = !classes.includes(asset.asset_classification.id)
          asset.checked = true

          // Look for children and do this recursively
          const immediateChildren = [...this.orgAssets].filter(({ parent_node }) => parent_node === asset.id).map(({ id }) => id)
          this.checkAssetsOfClasses(classes, immediateChildren)
        }
        // Disable selection by default if not checked
        if (!asset.checked) {
          asset.disabledSelection = true
        }
        return asset
      })
    },
    checkChildrenOfParentNode(parent, check = true) {
      const updatedAssets = [...this.orgAssets]
      updatedAssets.forEach((asset) => {
        if (asset.id === parent && asset.checked !== check) {
          asset.checked = check
        }
      })
      this.orgAssets = updatedAssets
      this.orgAssets.filter(({ parent_node }) => parent_node === parent).forEach(({ id }) => this.checkChildrenOfParentNode(id, check))
    },
    handleCheckedAsset(args) {
      const { checked, assetId } = args
      // Sync with the parent
      this.orgAssets = this.orgAssets.map((asset) => {
        if (asset.id === assetId) {
          asset.checked = checked
        }
        return asset
      })

      this.checkChildrenOfParentNode(assetId, checked)

      if (!checked) {
        this.uncheckParentsOfNode(assetId)
      }
    },
    uncheckParentsOfNode(node) {
      const parent = this.orgAssets.find(({ id }) => id === node)?.parent_node
      if (parent) {
        const updatedAssets = [...this.orgAssets]
        updatedAssets.forEach((asset) => {
          if (asset.id === parent && asset.checked) {
            asset.checked = false
          }
        })
        this.orgAssets = updatedAssets
        // Recrusive
        this.uncheckParentsOfNode(parent)
      }
    },
    emitCheckedAssets() {
      const assets = [...this.orgAssets].filter(({ checked }) => checked).map(({ id }) => id)
      this.$emit('selected', assets)
    },
    transformAssets(assets) {
      return [...assets]
        .filter(({ asset_classification }) => asset_classification?.id)
        .map((asset) => {
          // Initially only expand the top most asset
          asset.expandedChildren = !asset.parent_node
          asset.checked = false // Ensure assets are not checked by default
          asset.disabledSelection = false
          return asset
        })
    },
    transformClasses(classes) {
      const randomTwClasses = ['red', 'orange', 'yellow', 'green', 'blue', 'purple', 'rose', 'stone', 'amber', 'lime', 'teal', 'fuchsia']
      const coloredClasses = [...classes].map((assetClass, index) => {
        const colorIndex = index < randomTwClasses.length ? index : index % randomTwClasses.length
        assetClass.randomColor = randomTwClasses[colorIndex] || randomTwClasses[index - randomTwClasses.length]
        assetClass.checked = false // Ensure asset classes are not checked by default
        return assetClass
      })

      return [{ asset_class_id: 'all', asset_class_title: 'All', checked: false }, ...coloredClasses]
    },

    selectAllAssets() {
      // On select all assets
      this.onCheckAssetClass({ target: { checked: true } }, 'all')
      this.emitCheckedAssets()
    },
    deselectAllAssets(expandChildren = null) {
      // On deselect all assets
      this.orgAssets = this.orgAssets.map((asset) => {
        if (expandChildren !== null) {
          asset.expandedChildren = expandChildren
        }
        asset.checked = false
        asset.disabledSelection = false
        return asset
      })
      this.checkAllClassesCheckbox(false)
      this.checkOtherClassesCheckbox(false)
      this.emitCheckedAssets()
    },
    resetFilter() {
      this.deselectAllAssets()
      this.onCheckAssetClass({ target: { checked: true } }, 'all')
      this.$emit('enable')
    },
    togglePanel() {
      this.isOpen = !this.isOpen
      nextTick(() => {
        if (!this.isOpen) {
          this.emitCheckedAssets()
          this.killAllListeners()
        }
      })
    },
    killAllListeners() {
      document.body.removeEventListener('click', this.handleOutsideClick)
      document.body.removeEventListener('keydown', this.handleEscapeKey)
      document.removeEventListener('scroll', this.positionSelectorPanel, true)
      window.removeEventListener('resize', this.positionSelectorPanel, true)
    },
    closeSelectorAndEmitCheckedAssetsEvent() {
      this.isOpen = false
      this.emitCheckedAssets()
      this.killAllListeners()
    },
    handleOutsideClick(event) {
      if (!this.$refs[`assetsTreeSelector-${this.uid}`] || !this.$refs[`assetsTreePanel-${this.uid}`]) return

      if (!this.$refs[`assetsTreeSelector-${this.uid}`].contains(event.target) && !this.$refs[`assetsTreePanel-${this.uid}`].contains(event.target)) {
        this.closeSelectorAndEmitCheckedAssetsEvent()
      }
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape') {
        this.closeSelectorAndEmitCheckedAssetsEvent()
      }
    },
    positionSelectorPanel() {
      const assetsSelector = this.$refs[`assetsTreeSelector-${this.uid}`]
      const assetsPanel = this.$refs[`assetsTreePanel-${this.uid}`]

      if (!assetsSelector || !assetsPanel) return

      const { top, right, left } = assetsSelector.getBoundingClientRect()

      // if we didn't use nextTick, the assetsPanel width and height would be 0
      const { width: assetsPanelWidth } = assetsPanel.getBoundingClientRect()
      const { height: assetsSelectorHeight, width: assetsSelectorWidth } = assetsSelector.getBoundingClientRect()
      const { innerWidth } = window

      // Always have the panel below the selector
      this.selectorTop = top + assetsSelectorHeight

      if (left + assetsPanelWidth > innerWidth) {
        this.selectorLeft = innerWidth - assetsPanelWidth - 10
      }
      else {
        this.selectorLeft = right - assetsSelectorWidth
      }

      this.selectorWidth = assetsSelectorWidth
    },
  },
}
</script>
